import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import { Download, AnimatedSpinner } from '@unified-checkout/design-system/icons/index';
import { DataTable } from '@unified-checkout/design-system';
import type { IColumnMap } from 'libs/design-system/src/types/data_table';
import type {
  TExportData,
  TReportType,
  TReportsHeaderDateRanges,
} from '@app/dashboard/types/innerLayout/reports';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  downloadReport,
  getArchiveTableData,
} from '@app/dashboard/services/inner_layout/reports';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { getReportFileName } from '@app/dashboard/utils/getReportFileName';
import { getReportsTypeCellIcon } from '@app/dashboard/utils/getReportsTypeCellIcon';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import styles from './archive-tab.module.scss';

const TABLE_ID = 'archive_table';

export const ArchiveTab = ({ headerDateRange }: { headerDateRange: TReportsHeaderDateRanges }) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const { isLive } = useTestLiveModeStore();
  const { lang } = useLangStore();

  const [isError, setIsError] = useState(false);
  const [downloadingReportData, setDownloadingReportData] = useState<TExportData | null>(null);

  const {
    data: downloadResponse,
    isLoading: isDownloadingReport,
    error: downloadError,
    isError: isDownloadError,
  } = useQuery({
    queryKey: ['download-report', downloadingReportData?.download_url],
    queryFn: () => downloadReport(downloadingReportData?.download_url!),
    enabled: !!downloadingReportData?.download_url,
  });

  const columnsMap: IColumnMap[] = [
    {
      id: 'type',
      name: t('innerLayout.reportsPage.table.headers.type'),
      type: 'leading_icon',
      key: 'report_name',
      icon: (value) => getReportsTypeCellIcon(value as TReportType | 'custom')
    },
    {
      id: 'from',
      name: t('innerLayout.reportsPage.table.headers.from'),
      type: 'text',
      key: 'from_time',
    },
    {
      id: 'to',
      name: t('innerLayout.reportsPage.table.headers.to'),
      type: 'text',
      key: 'to_time',
    },
    {
      id: 'created',
      name: t('innerLayout.reportsPage.table.headers.created'),
      type: 'text',
      key: 'created_at',
    },
    {
      id: 'actions',
      name: '',
      type: {
        conditionsType: 'direct_value',
        conditions: {
          action_btns: {
            status: 'Download'
          },
          badge: {
            status: ['Generating', 'Failed ', 'Failed']
          },
        },
        default: 'badge',
      },
      key: {
        conditionsType: 'direct_value',
        conditions: {
          download: {
            status: 'Download'
          },
          pending: {
            status: 'Generating'
          },
          failed: {
            status: ['Failed ', 'Failed']
          }
        },
        default: 'pending',
      },
      label: {
        pending: {
          color: 'yellow',
          text: t('innerLayout.reportsPage.table.status.pending'),
        },
        failed: {
          color: 'red',
          text: t('innerLayout.reportsPage.table.status.failed'),
        }
      },
      actionBtnsProps: (row) => [
        {
          text: t('innerLayout.download'),
          variant: 'outlined',
          leadingIcon: isDownloadingReport && row?.download_url === downloadingReportData?.download_url
            ? AnimatedSpinner
            : Download,
          disabled: isDownloadingReport,
          color: 'secondary',
          onClickHandler: (row: TExportData) => onClickDownloadHandler(row)
        },
      ],
    },
  ];

  useEffect(() => {
    if (downloadResponse && downloadingReportData) {
      const contentDisposition =
        downloadResponse.headers['content-disposition'];
      const filename = getReportFileName(contentDisposition);
      let contentType = 'application/vnd.ms-excel';
      const format = filename?.endsWith('.xlsx')
        ? REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.XLSX
        : REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.CSV;

      if (filename && filename.endsWith('.xlsx')) {
        contentType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      const reportType = (downloadingReportData as TExportData & { report_name?: string }).report_name ?? downloadingReportData.type;
      const rowWithDates = downloadingReportData as TExportData & { from_time?: string; to_time?: string };
      const fromDate = downloadingReportData.from ?? rowWithDates.from_time ?? '';
      const toDate = downloadingReportData.to ?? rowWithDates.to_time ?? '';
      const dateRange = fromDate && toDate
        ? `${fromDate} - ${toDate}`
        : REPORTS_STATEMENTS_EVENTS_VALUES.DATE_RANGE.EXISTING;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_SUCCEEDED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: reportType,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: format,
        [SHARED_EVENTS_PARAMS.DATE_RANGE]: dateRange,
      });

      const fileType = downloadResponse.data?.type;
      const extractedExtension = fileType ? fileType.split('/').pop() : undefined;
      const extension = ['pdf', 'csv', 'xlsx'].includes(extractedExtension || '') ? extractedExtension : 'xlsx';

      const url = window.URL.createObjectURL(
        new Blob([downloadResponse.data], { type: contentType })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `report(${downloadingReportData?.id}).${extension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadingReportData(null);
    }
  }, [downloadResponse]);

  useEffect(() => {
    if (isDownloadError && downloadError && downloadingReportData) {
      const errorDescription =
        (typeof downloadError === 'object' &&
          downloadError !== null &&
          'response' in downloadError &&
          (downloadError as any).response?.data?.message) ||
        (downloadError as Error)?.message ||
        t('errors.somethingWentWrong');
      const reportType = (downloadingReportData as TExportData & { report_name?: string }).report_name ?? downloadingReportData.type;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_FAILED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: reportType,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.ERROR_DESCRIPTION]: errorDescription,
      });
      setDownloadingReportData(null);
    }
  }, [isDownloadError, downloadError, downloadingReportData]);

  const onClickDownloadHandler = (row: TExportData) => {
    if (row.download_url) {
      setDownloadingReportData(row);
    }
  };

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_ARCHIVE_REPORTS, isLive],
    [isLive]
  );

  const queryFunctionParams = useMemo(
    () => ({ is_live: isLive, platform: 'dashboard', ...headerDateRange }),
    [isLive, headerDateRange]
  );

  return (
    <div className={styles['archive-tab-wrapper']}>
      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
        }}
        pagination={{
          hideLabels: isMobile,
          type: "cursor_based",
          dataLocation: {
            next: "data.next",
            prev: "data.previous"
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getArchiveTableData,
          queryFunctionParams: queryFunctionParams,
        }}
        errorState={{
          onError: (error) => setIsError(error),
        }}
        emptyState={{
          text: t('innerLayout.reportsPage.table.body.noReportsYet'),
          description: t(
            'innerLayout.reportsPage.table.body.noReportsHint'
          ),
        }}
      />
    </div>
  );
};
